import { useReactiveVar } from '@apollo/client';
import {
  Box,
  Heading,
  Stack,
  VStack,
  motion,
  useTheme,
} from '@hover/blueprint';
import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';

import ExteriorOne from 'src/assets/img/homeownerApp/projects/createProject/exterior-1.jpg';
import ExteriorTwo from 'src/assets/img/homeownerApp/projects/createProject/exterior-2.jpg';
import ExteriorThree from 'src/assets/img/homeownerApp/projects/createProject/exterior-3.jpg';
import InteriorOne from 'src/assets/img/homeownerApp/projects/createProject/interior-1.jpg';
import InteriorTwo from 'src/assets/img/homeownerApp/projects/createProject/interior-2.jpg';
import InteriorThree from 'src/assets/img/homeownerApp/projects/createProject/interior-3.jpg';
import { FLOATING_NAV_HEIGHT } from 'src/modules/homeownerApp/components/FloatingNav/FloatingNav';
import {
  SpaceTypeSelection,
  SpaceTypeSelectionDescription,
} from 'src/modules/homeownerApp/components/Projects/ProjectCreation/SpaceTypeSelection';
import {
  closeUploadSheetAfterUpload,
  isUploadingOriginalImageVar,
} from 'src/modules/homeownerApp/components/UploadImage';
import { NotFoundSplash } from 'src/modules/homeownerApp/components/errors';
import { useGetPublicOrgInfoFromGlobalStorage } from 'src/modules/homeownerApp/helpers/partners';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { projectCreationStateSchema } from 'src/modules/homeownerApp/schemas';
import { Skeleton } from 'src/shared/components/Skeleton/Skeleton';
import {
  useAiSpaceTypesQuery,
  useGetProjectsQuery,
} from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import { CobrandedHeader } from '../../CobrandedHeader';

const MotionVStack = motion(VStack);

export const ProjectCreation = () => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const theme = useTheme();
  const { setCurrentMenu, loggedIn, user } = useHomeownerAppContext();
  const publicOrgInfo = useGetPublicOrgInfoFromGlobalStorage();
  const history = useHistory();
  const state = useMemo(
    () => projectCreationStateSchema.cast(history.location.state),
    [history.location.state],
  );

  const isUploading = useReactiveVar(isUploadingOriginalImageVar);
  const orgId = publicOrgInfo?.id || user?.orgs?.[0]?.id;

  const { data: spaceTypesData } = useAiSpaceTypesQuery({
    variables: { orgId },
    // Does not get shown to user on load
    ssr: false,
  });
  const { data: projectsData, loading: projectsLoading } =
    useGetProjectsQuery();
  const projects = projectsData?.projects ?? [];
  const availbleSpaceTypes = spaceTypesData?.aiSpaceTypes?.spaceTypes ?? [];
  const hasExterior = availbleSpaceTypes.some(
    spaceType => spaceType === 'exterior',
  );
  const hasInterior = availbleSpaceTypes.some(
    spaceType => spaceType !== 'exterior',
  );

  const onClick = useCallback(
    (spaceType: string) => () => {
      history.replace({
        pathname: history.location.pathname,
        state: { ...state, preSelectedSpaceType: spaceType },
      });
      closeUploadSheetAfterUpload(false);
      setCurrentMenu(HomeownerAppMenuNames.UPLOAD_PHOTO);
    },
    [history, setCurrentMenu, state],
  );

  const spaceTypeSelections = useMemo(
    () =>
      [
        {
          images: [ExteriorOne, ExteriorTwo, ExteriorThree],
          spaceType: 'exterior',
          translationKey: SpaceTypeSelectionDescription.EXTERIOR,
          onClick: onClick('exterior'),
          disabled: !hasExterior,
        },
        {
          images: [InteriorOne, InteriorTwo, InteriorThree],
          spaceType: 'interior',
          translationKey: SpaceTypeSelectionDescription.INTERIOR,
          onClick: onClick('interior'),
          disabled: !hasInterior,
        },
      ].filter(({ disabled }) => !disabled),
    [hasExterior, hasInterior, onClick],
  );

  if (!loggedIn) return <NotFoundSplash />;

  return (
    <>
      <CobrandedHeader
        cobrandId={publicOrgInfo?.id}
        cobrandLogo={publicOrgInfo?.brandLogoUrl ?? undefined}
        cobrandName={publicOrgInfo?.displayName}
      />
      <MotionVStack
        animate={{
          opacity: isUploading ? 0 : 1,
          transition: { delay: !isUploading ? 0.5 : 0 },
        }}
        pb={{
          base: `calc(${theme.space[500]} + ${FLOATING_NAV_HEIGHT}px)`,
          tablet: 500,
        }}
        pt={{ base: 500 }}
        px={{ base: 500, tablet: 600, desktop: 800 }}
      >
        <Box alignSelf="flex-start" pb={600} pt={700}>
          <Skeleton isLoaded={!projectsLoading}>
            <Heading>
              {t(
                projects.length
                  ? 'projects.projectCreation.selectYourProject'
                  : 'projects.projectCreation.selectYourFirst',
              )}
            </Heading>
          </Skeleton>
        </Box>
        <Stack
          flexDirection={{ base: 'column', desktop: 'row' }}
          gap={{ base: 400, tablet: 500 }}
          w={1}
        >
          {spaceTypeSelections.map(spaceType => (
            <SpaceTypeSelection
              data-testid={`${spaceType.spaceType}-selector`}
              key={spaceType.spaceType}
              {...spaceType}
            />
          ))}
        </Stack>
      </MotionVStack>
    </>
  );
};
